import localforage from 'localforage';

import type { PkMeshVertex } from '../utils/pkFromMesh';

/**
 * La malla de carreteras, guardada en la tablet.
 *
 * ── Qué sustituye y por qué ────────────────────────────────────────────────
 *
 * Hasta ahora la malla vivía en un `Map` a nivel de módulo dentro de
 * `usePrecacheRouteMesh`. Eso tenía tres agujeros, y los tres se notaban justo
 * donde más duele —sin cobertura, en carretera:
 *
 *   1. El `Map` se vacía al recargar y al reabrir la app. Si la tablet
 *      arrancaba ya sin señal, no se llenaba NUNCA.
 *   2. El sentido viajaba en las variables de la consulta, así que solo se
 *      cacheaba el del tramo activo. Al cambiar de sentido —uno de los tres
 *      momentos en que se pide el PK— fallo de caché y sin malla.
 *   3. Solo se precacheaba desde la pantalla de detalle del recorrido, y los
 *      modales del cronómetro se abren desde el `TimerOverlay`, que vive en el
 *      Header de toda la app.
 *
 * Este almacén los cierra los tres: sobrevive a reinicios, guarda LOS DOS
 * sentidos de cada vía y guarda TODAS las vías del usuario, no solo la del
 * recorrido abierto.
 *
 * ── Por qué hay un índice en memoria por delante ───────────────────────────
 *
 * `getCachedRoadMesh` tiene que ser SÍNCRONA: la consumen el trazado del mapa
 * (`MapWithSegments`) y el detector de cambio de sentido, que no pueden
 * esperar a IndexedDB en mitad de un render. Así que el disco se lee una vez
 * (`hydrateRoadMeshes`) y a partir de ahí se sirve de memoria. Lo que cambia
 * frente a antes es que ese índice ya no nace vacío en cada arranque.
 *
 * ── Por qué la clave lleva tenant y usuario ────────────────────────────────
 *
 * Mismo criterio que la semilla de Apollo (`getSeedKey`): la copia es duradera
 * y las tablets de campo se comparten. La cartografía no es sensible, pero el
 * hábito de indexar por identidad se mantiene por construcción, no por
 * acordarse de limpiar.
 */
export type RoadMesh = {
  roadId: string;
  roadName: string;
  direction: string;
  vertices: PkMeshVertex[];
};

type Guardado = {
  version: number;
  ownerKey: string;
  updatedAt: string;
  meshes: RoadMesh[];
};

const CLAVE = 'road-pk-mesh';

/**
 * Versión del formato guardado. Si algún día cambia la forma de `RoadMesh`, una
 * copia vieja en la tablet de un operario se leería con la forma nueva y
 * fallaría de maneras raras. Con esto se descarta y se vuelve a descargar, que
 * es barato.
 */
const VERSION = 1;

const store = localforage.createInstance({
  name: 'rois',
  storeName: 'road_pk_mesh',
});

/** Índice en memoria por carretera. Lo que permite la lectura síncrona. */
let porVia = new Map<string, RoadMesh[]>();

/**
 * Identidad cuya copia está ya volcada al índice, o `null` si no hay ninguna.
 *
 * Evita releer del disco lo que ya está en memoria: `hydrateRoadMeshes` se
 * llama en CADA resolución de PK sin cobertura, y cada lectura trae la malla
 * entera (~250 KB) para reconstruir el mismo índice.
 */
let hidratadaPara: string | null = null;

function indexar(meshes: RoadMesh[]): void {
  const nuevo = new Map<string, RoadMesh[]>();
  for (const m of meshes) {
    const lista = nuevo.get(m.roadId);
    if (lista) lista.push(m);
    else nuevo.set(m.roadId, [m]);
  }
  porVia = nuevo;
}

/** Reemplaza la copia guardada. No acumula: lo que llega ES la malla. */
export async function saveRoadMeshes(
  ownerKey: string,
  meshes: RoadMesh[]
): Promise<void> {
  const guardado: Guardado = {
    version: VERSION,
    ownerKey,
    updatedAt: new Date().toISOString(),
    meshes,
  };
  await store.setItem(CLAVE, guardado);
  indexar(meshes);
  hidratadaPara = ownerKey;
}

/**
 * Carga del disco al índice en memoria.
 *
 * Si la copia guardada es de otra identidad NO se sirve: se prefiere un
 * arranque frío antes que enseñarle a alguien la caché de otro.
 */
export async function hydrateRoadMeshes(ownerKey: string): Promise<void> {
  // Ya está en memoria y es de esta identidad: releer sería traer 250 KB del
  // disco para reconstruir el mismo índice.
  if (hidratadaPara === ownerKey) return;

  const guardado = await leerCopia(ownerKey);
  if (!guardado) return;
  indexar(guardado.meshes);
  hidratadaPara = ownerKey;
}

/**
 * Vuelca la copia al índice **y** dice su edad en días, de UNA sola lectura.
 *
 * Es lo que necesita `ensureRoadMesh` en cada arranque: hidratar y decidir si
 * toca refrescar. Pedirlo por separado leía la malla entera dos veces.
 *
 * Devuelve `null` si no hay copia utilizable para esta identidad.
 */
export async function hydrateAndAgeRoadMeshes(
  ownerKey: string
): Promise<number | null> {
  const guardado = await leerCopia(ownerKey);
  if (!guardado) return null;

  indexar(guardado.meshes);
  hidratadaPara = ownerKey;

  const ms = Date.now() - new Date(guardado.updatedAt).getTime();
  return Math.floor(ms / 86_400_000);
}

/**
 * La copia guardada, solo si es de ESTA identidad y de un formato que sabemos
 * leer. Punto único: así ninguna lectura se salta una de las dos condiciones.
 */
async function leerCopia(ownerKey: string): Promise<Guardado | null> {
  const guardado = await store.getItem<Guardado>(CLAVE);
  if (!guardado) return null;
  if (guardado.ownerKey !== ownerKey) return null;
  if (guardado.version !== VERSION) return null;
  return guardado;
}

/**
 * Una entrada por carretera Y sentido. **Es la lectura buena para calcular
 * PK**: proyectar sobre cada calzada por separado es lo único correcto.
 *
 * Sin `roadIds` devuelve todas las guardadas — lo que necesita el botón de
 * autolocalizar, que no sabe en qué vía está el operario.
 */
export function getCachedRoadMeshEntries(
  roadIds?: string[] | null
): RoadMesh[] {
  if (!roadIds) return [...porVia.values()].flat();
  return roadIds.flatMap((id) => porVia.get(id) ?? []);
}

/**
 * Una sola polilínea por vía, para DIBUJAR y para medir el rumbo del eje.
 *
 * 🔴 Devuelve UN sentido, nunca los dos concatenados. Las dos calzadas son
 * polilíneas paralelas y unirlas crea tramos que cruzan de una a otra: el
 * trazado saldría en zigzag y la proyección, sin sentido.
 *
 * ⚠️ NO es idéntico a lo de antes, y conviene saberlo: el precache traía el
 * sentido del TRAMO ACTIVO (`activeSegment?.direction ?? 'top'`), así que un
 * tramo `down` se dibujaba sobre su propia calzada. Aquí se sirve siempre la
 * misma. Las dos están a **18,2 m** medidos —irrelevante al zoom del mapa, y el
 * rumbo del eje es idéntico porque son paralelas—, pero si algún día hiciera
 * falta la calzada exacta, lo correcto es pasarle el sentido, no concatenar.
 *
 * Se elige la que MÁS vértices tenga, con `top` para desempatar. No es
 * cosmético: si una descarga dejó un sentido a medias, servir el completo es lo
 * que evita dibujar una línea coja. (Medido en la BD: los dos sentidos existen
 * en las 7 vías, con conteos casi iguales.)
 *
 * Para calcular PK, `getCachedRoadMeshEntries`, sentido a sentido.
 */
export function getCachedRoadMesh(roadId?: string | null): PkMeshVertex[] {
  if (!roadId) return [];
  const entradas = porVia.get(roadId) ?? [];
  if (entradas.length === 0) return [];

  let canonica = entradas[0];
  for (const m of entradas) {
    if (m.vertices.length > canonica.vertices.length) canonica = m;
    else if (
      m.vertices.length === canonica.vertices.length &&
      m.direction === 'top'
    ) {
      canonica = m;
    }
  }
  return canonica.vertices;
}

/**
 * Las mallas de varias carreteras indexadas por id, que es la forma que
 * consumen `useGpsToPK` (vía `pkFromMeshes`) y el modal de pausa (vía
 * `buildRoadStretches`, que reconstruye por qué carreteras pasó el tramo).
 *
 * Solo devuelve las que YA tienen malla: una carretera sin precachear no debe
 * entrar como candidata vacía, o competiría con las que sí tienen datos.
 */
export function getCachedRoadMeshes(
  roadIds?: string[] | null
): Record<string, PkMeshVertex[]> {
  const out: Record<string, PkMeshVertex[]> = {};
  for (const roadId of roadIds ?? []) {
    const vertices = getCachedRoadMesh(roadId);
    if (vertices.length > 0) out[roadId] = vertices;
  }
  return out;
}

/** Borra la copia y el índice. Se llama al cerrar sesión. */
export async function clearRoadMeshes(): Promise<void> {
  await store.removeItem(CLAVE);
  porVia = new Map();
  hidratadaPara = null;
}

/**
 * Días desde la última descarga, o `null` si no hay copia para esta identidad.
 * Lo consulta `ensureRoadMesh` para decidir si toca refrescar.
 */
export async function meshAgeDays(ownerKey: string): Promise<number | null> {
  const guardado = await leerCopia(ownerKey);
  if (!guardado) return null;
  const ms = Date.now() - new Date(guardado.updatedAt).getTime();
  return Math.floor(ms / 86_400_000);
}

/** Solo para tests: simula el arranque en frío sin tocar el disco. */
export function clearRoadMeshMemoryCache(): void {
  porVia = new Map();
  hidratadaPara = null;
}
